import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { OptionType } from '@myn/mui';

import type { Label } from '../../api/users/users.types.js';
import EmailView from '../../organisms/UserModalNew/EmailView.jsx';
import type { UserModalViewType } from '../../organisms/UserModalNew/userModal.types.js';
import UserView from '../../organisms/UserModalNew/UserView.jsx';
import UserViewOld from '../../organisms/UserModalNew/UserViewOld.jsx';
import { UserModalType } from '../../organisms/UserModalNew/validation.js';

export type UserModalViewProps = {
  currentView: UserModalViewType;
  formInstance: UseFormReturn<UserModalType>;
  isReadonlyPermission: boolean;
  roleOptions: OptionType[];
  clientsOptions: OptionType[] | null;
  assignedClients: number[] | null;
  setAssignedClients: Dispatch<SetStateAction<number[] | null>>;
  isLoading: boolean;
  isSaving: boolean;
  initialTags: Label[] | null;
};

const useUserModalView = ({
  currentView,
  formInstance,
  isReadonlyPermission,
  roleOptions,
  clientsOptions,
  assignedClients,
  setAssignedClients,
  isLoading,
  isSaving,
  initialTags,
}: UserModalViewProps) => {
  const userModalView = useMemo(
    () => ({
      emailView: () => <EmailView formInstance={formInstance} />,
      editUserView: () => (
        <UserView
          formInstance={formInstance}
          isReadonlyPermission={isReadonlyPermission}
          roleOptions={roleOptions}
          clientsOptions={clientsOptions}
          assignedClients={assignedClients}
          setAssignedClients={setAssignedClients}
          isLoading={isLoading}
          isSaving={isSaving}
          view={currentView}
          initialTags={initialTags}
        />
      ),
      existingUserView: () => (
        <UserViewOld
          formInstance={formInstance}
          isReadonlyPermission={isReadonlyPermission}
          roleOptions={roleOptions}
          clientsOptions={clientsOptions}
          assignedClients={assignedClients}
          setAssignedClients={setAssignedClients}
          isLoading={isLoading}
          isSaving={isSaving}
          view={currentView}
        />
      ),
      nonExistingUserView: () => (
        <UserViewOld
          formInstance={formInstance}
          isReadonlyPermission={isReadonlyPermission}
          roleOptions={roleOptions}
          clientsOptions={clientsOptions}
          assignedClients={assignedClients}
          setAssignedClients={setAssignedClients}
          isLoading={isLoading}
          isSaving={isSaving}
          view={currentView}
        />
      ),
    }),
    [
      currentView,
      formInstance,
      isReadonlyPermission,
      roleOptions,
      clientsOptions,
      assignedClients,
      setAssignedClients,
      isLoading,
      isSaving,
      initialTags,
    ],
  );

  return userModalView[currentView]();
};

export default useUserModalView;
